import { createContext, useContext, useEffect, useState } from "react";

const AppContext = createContext(null);

const STORAGE_KEYS = {
  user: "warranty.user",
  inspections: "warranty.inspections",
  theme: "warranty.theme",
  fontSize: "warranty.fontSize",
  formLayout: "warranty.formLayout",
  setupCompleted: "warranty.setupCompleted",
};

const readStored = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : raw;
  } catch {
    return fallback;
  }
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.user);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [inspections, setInspections] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.inspections);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const [theme, setThemeState] = useState(() =>
    readStored(STORAGE_KEYS.theme, "light") === "dark" ? "dark" : "light"
  );

  const [fontSize, setFontSizeState] = useState(() =>
    readStored(STORAGE_KEYS.fontSize, "normal") === "large" ? "large" : "normal"
  );

  const [formLayout, setFormLayoutState] = useState(() =>
    readStored(STORAGE_KEYS.formLayout, "single") === "slides" ? "slides" : "single"
  );

  const [setupCompleted, setSetupCompletedState] = useState(
    () => readStored(STORAGE_KEYS.setupCompleted, "false") === "true"
  );

  // Apply theme class to <html>
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem(STORAGE_KEYS.theme, theme);
  }, [theme]);

  // Apply large-text class to <html>
  useEffect(() => {
    const root = document.documentElement;
    if (fontSize === "large") root.classList.add("large-text");
    else root.classList.remove("large-text");
    localStorage.setItem(STORAGE_KEYS.fontSize, fontSize);
  }, [fontSize]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.formLayout, formLayout);
  }, [formLayout]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.setupCompleted, String(setupCompleted));
  }, [setupCompleted]);

  useEffect(() => {
    if (user) localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
    else localStorage.removeItem(STORAGE_KEYS.user);
  }, [user]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.inspections, JSON.stringify(inspections));
  }, [inspections]);

  const login = (engineerId, name) => {
    setUser({
      id: engineerId || `ENG-${Math.floor(Math.random() * 9000 + 1000)}`,
      name: name || engineerId || "Ingeniero",
      loggedAt: new Date().toISOString(),
    });
  };

  const logout = () => setUser(null);

  const saveInspection = (inspection) => {
    setInspections((prev) => {
      const exists = prev.find((i) => i.id === inspection.id);
      if (exists) return prev.map((i) => (i.id === inspection.id ? inspection : i));
      return [inspection, ...prev];
    });
  };

  const getInspection = (id) => inspections.find((i) => i.id === id);

  const setTheme = (t) => setThemeState(t === "dark" ? "dark" : "light");
  const toggleTheme = () => setThemeState((t) => (t === "dark" ? "light" : "dark"));

  const setFontSize = (s) => setFontSizeState(s === "large" ? "large" : "normal");
  const setFormLayout = (l) => setFormLayoutState(l === "slides" ? "slides" : "single");
  const completeSetup = () => setSetupCompletedState(true);
  const resetSetup = () => setSetupCompletedState(false);

  return (
    <AppContext.Provider
      value={{
        user,
        login,
        logout,
        inspections,
        saveInspection,
        getInspection,
        theme,
        setTheme,
        toggleTheme,
        fontSize,
        setFontSize,
        formLayout,
        setFormLayout,
        setupCompleted,
        completeSetup,
        resetSetup,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
